import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Product } from '@/types';
import { api, ApiClientError } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { useCart, MAX_QTY, availableForSize, itemKey, isProductItem } from '@/features/cart/CartContext';
import { useUI } from '@/client/ui/UIContext';
import { useI18n } from '@/client/i18n/I18nContext';
import { track } from '@/client/lib/analytics';
import { Price } from '@/components/Price';
import { CARONote } from '@/components/CARONote';
import { FramedImage } from '@/components/FramedImage';
import { Turnstile, TURNSTILE_ENABLED } from '@/components/Turnstile';
import { Breadcrumb } from '@/components/Breadcrumb';
import { LoadingState, ErrorState } from '@/components/States';
import { ProductArticles } from '@/components/ProductArticles';
import { Newsletter } from '@/features/newsletter/Newsletter';
import { RestockAlert } from '@/features/restock/RestockAlert';
import { acceptsRestockAlert, isSoldOut, isSoldPiece, showsSoldOut } from '@/lib/catalog/availability';
import { FINDS_PATH, findDetails, isFind } from '@/lib/catalog/finds';
import { productPageText, isIndexableProduct } from '@/lib/seo';
import { localizeProduct } from '@/lib/i18n-content';
import { lineKey, pieceDeliveryCents, productSizes } from '@/lib/cart';
import { applyFuelSurcharge, formatDeliveryEstimate, FUEL_SURCHARGE_PERCENT, hasDeliveryEstimate } from '@/lib/shipping';
import { formatMoney } from '@/lib/money';
import { lp } from '@/client/i18n/locale';
import { useLogicalParams } from '@/client/i18n/useLogicalParams';

export function ProductDetailPage() {
  const { slug, category } = useLogicalParams<'slug' | 'category'>('/shop/:category/:slug');
  const [product, setProduct] = useState<Product | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  // Chosen size (sized garments only); required before adding to the cart.
  const [size, setSize] = useState<string | null>(null);
  const { add, items } = useCart();
  const { openCart } = useUI();
  const { t, locale } = useI18n();

  // Localized <head>, same title/description/robots as the Worker (lib/seo;
  // falls back to Spanish per field). Draft/archived pieces are noindex.
  const metaProduct = product ? localizeProduct(product, locale) : null;
  useMeta({
    ...(metaProduct ? productPageText(metaProduct, locale) : { title: t('meta.product.title') }),
    path: `/shop/${category}/${slug}`,
    type: 'product',
    image: product?.image ?? undefined,
    noindex: product ? !isIndexableProduct(product) : undefined,
  });

  useEffect(() => {
    if (!slug) return;
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    setSize(null);
    api
      .product(slug)
      .then((p) => {
        setProduct(p);
        track('view_product', { id: p.id, name: p.name });
      })
      .catch((e) => {
        if (e instanceof ApiClientError && e.code === 'not_found') setNotFound(true);
        else setNotFound(true);
      });
  }, [slug]);

  // Size variants (`metadata.sizes`); [] = unsized product (unchanged flow).
  const sizes = product ? productSizes(product) : [];
  const sized = sizes.length > 0;
  // Units already in the selection: this exact line (product, or product + the
  // selected size), and across all sizes of the product (shared total stock).
  const inCart = product
    ? items.find((i) => itemKey(i) === lineKey(product.id, size ?? undefined))?.quantity ?? 0
    : 0;
  const inCartProduct = product
    ? items
        .filter(isProductItem)
        .filter((i) => i.product.id === product.id)
        .reduce((n, i) => n + i.quantity, 0)
    : 0;
  // Availability = server value (raw stock − units reserved by other carts).
  // Non-tracked items keep the generic ceiling. Pre-order pieces (reasortimiento)
  // sell before stock arrives, so they also use the open ceiling. Subtract what's
  // already in cart.
  const isPreorder = product?.metadata.preorder === true;
  // Stock-based for every product (no "untracked / unlimited" mode); pre-orders
  // use the open ceiling since they sell before stock arrives.
  // For a sized product with a size chosen: that size's units (capped by the
  // total, and by what the other sizes in the cart already take from it).
  const available = product
    ? isPreorder
      ? MAX_QTY
      : sized && size
        ? availableForSize(product, size)
        : product.stockQuantity
    : 0;
  const maxAddable =
    sized && !isPreorder && product
      ? Math.max(0, Math.min(available - inCart, product.stockQuantity - inCartProduct))
      : Math.max(0, available - inCart);
  // A size is sold out when it has no units of its own (pre-orders never are).
  const sizeSoldOut = (s: string) => !!product && !isPreorder && availableForSize(product, s) <= 0;

  // Never let the selector exceed what can still be added.
  useEffect(() => {
    setQty((q) => Math.min(Math.max(1, q), Math.max(1, maxAddable)));
  }, [maxAddable]);

  if (notFound) {
    return <ErrorState title={t('pdp.notFoundTitle')} message={t('pdp.notFoundMsg')} />;
  }
  if (!product) return <LoadingState />;

  // Localized view for display (name/subtitle/description/caroNote/metadata).
  const view = localizeProduct(product, locale);
  const isEnquiry = view.checkoutMode === 'enquiry';
  const m = view.metadata;
  // Nothing left to sell (lib/catalog/availability, shared with the prerender
  // and the JSON-LD): no units (sized: the whole product, not just the selected
  // size) or marked `sold_out` by hand even with stock; pre-orders never are.
  const soldOut = isSoldOut(product);
  // A sold-out direct piece swaps its buy block for «Agotado» / «Pieza vendida».
  const soldOutMode = showsSoldOut(product);
  const soldPiece = isSoldPiece(product);
  // The checkout only sells `active` pieces (lib/cart isPurchasable): any other
  // status (e.g. on_hold with stock) keeps the button off, never a dead end.
  // A scheduled drop is gated by the server only (API 404 + priceCart), never
  // by the visitor's clock, which may run behind at the drop's instant.
  const purchasable = product.status === 'active';
  // Sized product, nothing chosen yet: the add button waits for a size.
  const needsSize = sized && !size;
  // Delivery-delay notice text for pre-order pieces (per-product override → default).
  const preorderNotice = isPreorder ? (m.preorderNote?.trim() || t('pdp.preorderNotice')) : null;
  // Show a discreet "Disponible" (no unit count — we never reveal how many are
  // left) ONLY when few units remain (≤ 10): a soft scarcity cue for tracked
  // pieces running low. Plenty-in-stock and pre-order pieces show nothing here
  // (pre-orders show their own delay notice instead).
  const LOW_STOCK_HINT_AT = 10;
  const showAvailable =
    purchasable && !soldOut && !isPreorder && maxAddable >= 1 && available <= LOW_STOCK_HINT_AT;
  // Link to the parent category: alternatives for a sold-out piece.
  const categoryLabel = t(`nav.${product.category}`);
  // A find (lib/catalog/finds): its own breadcrumb level and blocks («La
  // historia», «Medidas», «Estado»), the same text as the Worker prerender.
  const find = isFind(product);
  const details = findDetails(view, t);

  // Full image set = main image + gallery, de-duplicated by URL.
  const images: { url: string; alt: string }[] = [];
  const seen = new Set<string>();
  if (view.image) {
    images.push({ url: view.image, alt: view.name });
    seen.add(view.image);
  }
  for (const g of view.gallery) {
    if (g.url && !seen.has(g.url)) {
      images.push({ url: g.url, alt: g.alt || view.name });
      seen.add(g.url);
    }
  }
  const current = images[Math.min(activeImg, images.length - 1)];

  return (
    <div className="container">
      {/* Product JSON-LD is injected by the Worker into <head> (pageMeta), with
          the checkout's D1 shipping zones: not repeated here. */}
      <Breadcrumb
        items={[
          { name: t('common.home'), path: '/' },
          { name: t('nav.shop'), path: '/shop' },
          find
            ? { name: t('finds.title'), path: FINDS_PATH }
            : { name: t(`nav.${product.category}`), path: `/shop/${product.category}` },
          { name: view.name },
        ]}
      />

      <div className="pdp" style={{ paddingBottom: 'var(--space-6)' }}>
        <div>
          <div className="pdp__gallery">
            {current ? (
              <FramedImage src={current.url} alt={current.alt} fallback={product.brand ?? 'CARO'} />
            ) : (
              <span className="card__placeholder">{product.brand ?? 'CARO'}</span>
            )}
          </div>
          {images.length > 1 ? (
            <div className="pdp__thumbs">
              {images.map((img, i) => (
                <button
                  key={img.url}
                  type="button"
                  className={`pdp__thumb${i === activeImg ? ' is-active' : ''}`}
                  aria-label={img.alt}
                  aria-current={i === activeImg}
                  onClick={() => setActiveImg(i)}
                >
                  <img src={img.url} alt={img.alt} loading="lazy" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="pdp__info stack">
          {product.brand ? <p className="eyebrow">{product.brand}</p> : null}
          <h1>{view.name}</h1>
          {/* A find has no subtitle or CARO note: its story carries that voice. */}
          {view.subtitle && !find ? <p className="lede">{view.subtitle}</p> : null}

          {!product.dataVerified ? (
            <span className="badge badge--placeholder">
              {t('pdp.unverifiedLong')}
            </span>
          ) : null}

          {!isEnquiry ? (
            <div style={{ fontSize: 'var(--fs-xl)' }}>
              <Price
                cents={product.priceCents}
                currency={product.currency}
                compareAtCents={product.compareAtPriceCents}
                tbc={!product.dataVerified || product.priceCents <= 0}
              />
            </div>
          ) : null}

          {/* CARO editorial recommendation — visually distinct */}
          {view.caroNote && !find ? <CARONote note={view.caroNote} /> : null}

          {isEnquiry ? (
            <EnquiryForm productSlug={product.slug} />
          ) : soldOutMode ? (
            <>
              <p className="sold-note" role="status">
                <span className="sold-note__tag">
                  {soldPiece ? t('pdp.soldPieceTitle') : t('pdp.soldOutTitle')}
                </span>
                {soldPiece ? <span className="sold-note__text">{t('pdp.soldPieceText')}</span> : null}
              </p>
              {acceptsRestockAlert(product) ? <RestockAlert key={product.slug} productSlug={product.slug} /> : null}
              <p style={{ fontSize: 'var(--fs-sm)' }}>
                <Link to={lp(`/shop/${product.category}`)} className="link-underline">
                  {t('pdp.alternatives').replace('{category}', categoryLabel)}
                </Link>
              </p>
            </>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-2)' }}>
              {sized ? (
                <fieldset className="size-picker">
                  <legend className="size-picker__legend">{t('pdp.size')}</legend>
                  <div className="size-picker__options">
                    {sizes.map((s) => {
                      const out = sizeSoldOut(s);
                      return (
                        <label
                          key={s}
                          className={`size-picker__option${size === s ? ' is-selected' : ''}${out ? ' is-disabled' : ''}`}
                        >
                          <input
                            type="radio"
                            className="size-picker__input"
                            name="pdp-size"
                            value={s}
                            checked={size === s}
                            disabled={out}
                            onChange={() => setSize(s)}
                          />
                          <span>{s}</span>
                          {out ? <span className="size-picker__out">{t('pdp.sizeSoldOut')}</span> : null}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ) : null}
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="qty">
                  <button
                    type="button"
                    aria-label={t('a11y.qtyDecrease')}
                    disabled={qty <= 1}
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                  >
                    −
                  </button>
                  <span>{qty}</span>
                  <button
                    type="button"
                    aria-label={t('a11y.qtyIncrease')}
                    disabled={qty >= maxAddable}
                    onClick={() => setQty((q) => Math.min(maxAddable, q + 1))}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="btn"
                  disabled={!purchasable || product.priceCents <= 0 || maxAddable < 1 || needsSize}
                  aria-describedby={purchasable && needsSize ? 'pdp-choose-size' : undefined}
                  onClick={() => {
                    add(view, qty, size ?? undefined);
                    openCart();
                  }}
                >
                  {t('pdp.addToSelection')}
                </button>
              </div>
              {!purchasable ? (
                <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('pdp.unavailable')}</p>
              ) : needsSize ? (
                <p id="pdp-choose-size" className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                  {t('pdp.chooseSize')}
                </p>
              ) : maxAddable < 1 ? (
                <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('pdp.allInCart')}</p>
              ) : showAvailable ? (
                <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('pdp.available')}</p>
              ) : null}
              {preorderNotice ? (
                <p className="preorder-note">
                  <span className="preorder-note__tag">{t('product.preorderBadge')}</span>
                  {preorderNotice}
                </p>
              ) : null}
            </div>
          )}

          {/* Direct-checkout collectible pieces can still offer a "prefer to ask
              first?" path: the same enquiry form, tucked below the buy controls. */}
          {!isEnquiry && !soldOutMode && m.allowEnquiry ? (
            <details
              className="enquiry-disclosure"
              style={{ marginTop: 'var(--space-3)' }}
              onToggle={(e) => setEnquiryOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-sm)' }}>
                {t('pdp.enquireAlso')}
              </summary>
              {/* Mount the form (and its Turnstile widget) only once the disclosure
                  is open, so Turnstile renders into a visible container and its
                  challenge script isn't loaded on every product view. */}
              {enquiryOpen ? <EnquiryForm productSlug={product.slug} /> : null}
            </details>
          ) : null}

          {m.shippingNote && !soldOutMode ? (
            <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {m.shippingNote}
            </p>
          ) : null}

          {/* Indicative timings (SHIPPING_TIMES, same line as the prerender and
              the JSON-LD deliveryTime). Not for pre-orders (their delay notice
              rules), enquiry pieces (quotation) nor sold-out ones. */}
          {hasDeliveryEstimate(view) ? (
            <p className="text-muted delivery-estimate" style={{ fontSize: 'var(--fs-sm)' }}>
              {formatDeliveryEstimate('all', t)}
            </p>
          ) : null}

          {/* Large piece while the temporary fuel surcharge is on: the base
              prices in shippingNote are no longer what checkout charges. */}
          {FUEL_SURCHARGE_PERCENT > 0 && (m.deliverySpainCents ?? 0) > 0 && !soldOutMode ? (
            <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {t('pdp.fuelShipping')}{' '}
              {(
                [
                  ['es', 'pdp.zoneSpain'],
                  ['europe', 'pdp.zoneEurope'],
                  ['international', 'pdp.zoneInternational'],
                ] as const
              )
                .map(
                  ([zone, key]) =>
                    `${t(key)} ${formatMoney(applyFuelSurcharge(pieceDeliveryCents(m, zone)), product.currency)}`,
                )
                .join(' · ')}
              {t('pdp.perPiece')}
            </p>
          ) : null}

          {details ? (
            <div className="find-details" style={{ marginTop: 'var(--space-4)' }}>
              {details.story ? (
                <>
                  <h2 className="eyebrow">{details.story.title}</h2>
                  <p>{details.story.text}</p>
                </>
              ) : null}
              {details.specs.length ? (
                <dl>
                  {details.specs.map((s) => (
                    <div key={s.label} className="spec">
                      <dt>{s.label}</dt>
                      <dd>{s.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>
          ) : null}

          {/* Official / manufacturer information — separate from CARO's note */}
          <div style={{ marginTop: 'var(--space-4)' }}>
            <p className="eyebrow">{t('pdp.officialInfo')}</p>
            {view.description ? <p>{view.description}</p> : null}
            <dl>
              {m.size ? (
                <div className="spec">
                  <dt>{t('pdp.spec.size')}</dt>
                  <dd>{m.size}</dd>
                </div>
              ) : null}
              {m.benefits && m.benefits.length ? (
                <div className="spec">
                  <dt>{t('pdp.spec.benefits')}</dt>
                  <dd>{m.benefits.join(' · ')}</dd>
                </div>
              ) : null}
              {m.howToUse ? (
                <div className="spec">
                  <dt>{t('pdp.spec.howToUse')}</dt>
                  <dd>{m.howToUse}</dd>
                </div>
              ) : null}
              {m.ingredients ? (
                <div className="spec">
                  <dt>{t('pdp.spec.ingredients')}</dt>
                  <dd>{m.ingredients}</dd>
                </div>
              ) : null}
              {m.inci ? (
                <div className="spec">
                  <dt>{t('pdp.spec.inci')}</dt>
                  <dd>{m.inci}</dd>
                </div>
              ) : null}
              {m.edition ? (
                <div className="spec">
                  <dt>{t('pdp.spec.edition')}</dt>
                  <dd>{m.edition}</dd>
                </div>
              ) : null}
            </dl>
            {m.warnings ? (
              <p className="text-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 'var(--space-3)' }}>
                {m.warnings}
              </p>
            ) : null}
            {product.category === 'beauty' ? (
              <p className="text-muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--space-3)' }}>
                {t('pdp.disclaimer')}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {/* A sold one-off piece will not come back: no alert. A sold find offers
          the «Hallazgos» notices; any other one-off piece, La carta. */}
      {soldOutMode && soldPiece ? (
        find ? (
          <Newsletter
            lists={['finds']}
            defaultLists={['finds']}
            eyebrow={t('finds.letterEyebrow')}
            title={t('pdp.soldFindLetterTitle')}
            body={t('finds.letterBody')}
          />
        ) : (
          <Newsletter lists={['letter']} title={t('pdp.soldPieceLetterTitle')} />
        )
      ) : null}
      <ProductArticles productSlug={product.slug} />
    </div>
  );
}

function EnquiryForm({ productSlug }: { productSlug: string }) {
  const { t, locale } = useI18n();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !tsToken) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      await api.enquiry({ name, email, message, productSlug, company, locale, turnstileToken: tsToken });
      track('contact', { kind: 'enquiry' });
      setStatus('done');
    } catch {
      setStatus('error');
      setTsReset((n) => n + 1);
    }
  }

  if (status === 'done') {
    return (
      <div className="caro-note">
        <p className="caro-note__body">{t('pdp.enquireThanks')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="stack" style={{ marginTop: 'var(--space-2)' }}>
      <p className="eyebrow">{t('pdp.enquireTitle')}</p>
      <div className="form-grid form-grid--2">
        <div className="field">
          <label htmlFor="eq-name">{t('common.name')}</label>
          <input id="eq-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="eq-email">{t('common.email')}</label>
          <input id="eq-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="eq-msg">{t('common.message')}</label>
        <textarea id="eq-msg" rows={4} required value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div className="honeypot" aria-hidden>
        <input tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
      </div>
      <Turnstile onVerify={setTsToken} resetSignal={tsReset} />
      <button type="submit" className="btn" disabled={status === 'loading'}>
        {status === 'loading' ? t('common.sending') : t('pdp.sendRequest')}
      </button>
      {status === 'error' ? (
        <p style={{ color: 'var(--color-danger)' }}>{t('common.sendError')}</p>
      ) : null}
    </form>
  );
}
